import api from '@/admin-api'

const STALE_AFTER_MS = 24 * 60 * 60 * 1000 // 24 hours

// Module-scoped reference to the EventSource so we can close/restart it
let eventSource = null

/**
 * Map a raw relatie API object to our store representation.
 * Centralized here so every action/mutation uses the same shape.
 */
function mapRelatie (r) {
  return {
    value: r.Id,
    text: r.Naam,
    isKeuringsinstantie: !!r.IsKeuringsinstantie,
    isOnderhoudspartij: !!r.IsOnderhoudsfirma
  }
}

export default {
  namespaced: true,
  state: {
    relaties: [],
    lastLoaded: null,
    sseConnected: false
  },
  getters: {
    relatieNaam: (state) => (id) => {
      const relatie = state.relaties.find(r => r.value === id)
      return relatie ? relatie.text : null
    },
    relaties: (state) => state.relaties,
    keuringsinstanties: (state) => state.relaties
      .filter(r => r.isKeuringsinstantie)
      .map(r => ({ value: r.text, text: r.text })),
    onderhoudspartijen: (state) => state.relaties
      .filter(r => r.isOnderhoudspartij),
    isStale: (state) => {
      if (!state.lastLoaded) return true
      return Date.now() - state.lastLoaded > STALE_AFTER_MS
    },
    sseConnected: (state) => state.sseConnected
  },
  mutations: {
    setRelaties (state, relaties) {
      state.relaties = relaties
      state.lastLoaded = Date.now()
    },
    addRelatie (state, relatie) {
      // Avoid duplicates (e.g., when the same change comes back via SSE)
      if (state.relaties.some(r => r.value === relatie.value)) return
      state.relaties.push(relatie)
    },
    updateRelatie (state, relatie) {
      const idx = state.relaties.findIndex(r => r.value === relatie.value)
      if (idx === -1) {
        state.relaties.push(relatie)
      } else {
        // Splice to ensure Vue 2 reactivity
        state.relaties.splice(idx, 1, { ...state.relaties[idx], ...relatie })
      }
    },
    removeRelatie (state, id) {
      const idx = state.relaties.findIndex(r => r.value === id)
      if (idx !== -1) state.relaties.splice(idx, 1)
    },
    setSseConnected (state, value) {
      state.sseConnected = !!value
    }
  },
  actions: {
    load ({ commit }) {
      return api.get('/relaties').then((response) => {
        const index = response.data.relaties.map(mapRelatie)
        commit('setRelaties', index)
      })
    },
    refreshIfStale ({ dispatch, getters }) {
      if (getters.isStale) return dispatch('load')
    },
    ensureLoaded ({ dispatch, getters }, id) {
      if (!getters.relatieNaam(id)) return dispatch('load')
    },

    /**
     * Optimistically add a relatie to the store after a successful POST.
     * Expects the raw API relatie object (with Id, Naam, IsKeuringsinstantie, IsOnderhoudsfirma).
     */
    relatieCreated ({ commit }, rawRelatie) {
      commit('addRelatie', mapRelatie(rawRelatie))
    },

    /**
     * Optimistically update a relatie in the store after a successful PUT.
     * Expects the raw API relatie object.
     */
    relatieUpdated ({ commit }, rawRelatie) {
      commit('updateRelatie', mapRelatie(rawRelatie))
    },

    /**
     * Optimistically remove a relatie from the store after a successful DELETE.
     */
    relatieDeleted ({ commit }, id) {
      commit('removeRelatie', id)
    },

    /**
     * Open an SSE connection to listen for cache-invalidation events from the API.
     * The browser will send the HttpOnly auth cookie thanks to `withCredentials: true`.
     */
    connectSse ({ commit, dispatch }) {
      if (typeof window === 'undefined' || typeof EventSource === 'undefined') return
      if (eventSource) return // already connected

      const baseURL = process.env.VUE_APP_TPA_API_URL || ''
      const url = `${baseURL.replace('/index.cfm', '')}/system-events/`

      try {
        eventSource = new EventSource(url, { withCredentials: true })
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[relaties] Could not open SSE connection:', e)
        return
      }

      eventSource.onopen = () => {
        commit('setSseConnected', true)
      }

      eventSource.addEventListener('relaties_updated', () => {
        dispatch('load')
      })

      eventSource.onerror = (e) => {
        // eslint-disable-next-line no-console
        console.warn('[relaties] SSE error, browser will auto-reconnect:', e)
        commit('setSseConnected', false)
      }
    },

    /**
     * Cleanly close the SSE connection (e.g., on logout).
     */
    disconnectSse ({ commit }) {
      if (eventSource) {
        eventSource.close()
        eventSource = null
      }
      commit('setSseConnected', false)
    }
  }
}
